import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ApiRequestError } from '../../../lib/api'
import { useAuthStore } from '../../../stores/authStore'
import { useOnboardingV2Store } from '../../../stores/onboardingV2Store'
import OnboardingLayout from './OnboardingLayout'
import PrimaryButton from './PrimaryButton'
import Wordmark from './Wordmark'
import { stepsForMode } from './steps'

interface WelcomeScreenProps {
  onGetStarted: () => void
}

export default function WelcomeScreen({ onGetStarted }: WelcomeScreenProps) {
  const navigate = useNavigate()
  const fetchUser = useAuthStore((s) => s.fetchUser)
  const capabilityMode = useOnboardingV2Store((s) => s.state?.capability_mode ?? 'managed')
  const skip = useOnboardingV2Store((s) => s.skip)
  const [skipping, setSkipping] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSkip = async () => {
    setSkipping(true)
    setError(null)
    try {
      await skip()
      await fetchUser()
      navigate('/library', { replace: true })
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.error.message : "Couldn't skip — try again.")
    } finally {
      setSkipping(false)
    }
  }

  return (
    <OnboardingLayout
      steps={stepsForMode(capabilityMode)}
      activeKey="welcome"
      contentClassName="flex items-center justify-center"
    >
      <div className="mx-auto w-full max-w-[600px] text-center">
        <Wordmark size="lg" className="mb-8" />
        <p className="mb-10 font-body text-base leading-relaxed text-ob-on-surface md:text-lg">
          Turn your CVs and notes into a structured library of professional proof — then
          assemble a tailored CV for any role in minutes.
        </p>
        <div className="flex flex-col items-center gap-4">
          <PrimaryButton onClick={onGetStarted}>Get started</PrimaryButton>
          <button
            type="button"
            onClick={handleSkip}
            disabled={skipping}
            className="font-body text-sm text-ob-secondary transition-colors hover:text-ob-ink disabled:opacity-50"
          >
            {skipping ? 'Skipping…' : 'Skip for now'}
          </button>
          {error && (
            <p className="font-body text-sm text-ob-error" role="alert">
              {error}
            </p>
          )}
        </div>
      </div>
    </OnboardingLayout>
  )
}
